<template>
    <Modal
        size="lg"
        :title="$t('targets.automaticGoalsCalculation')"
        :okTitle="$t('modal.save')"
        :okDisabled="disabledOkButton"
        :cancelDisabled="disabledCancelButton"
        :cancelTitle="$t('modal.cancel')"
        :noCloseOnEsc="true"
        :noCloseOnBackdrop="true"
        isModalRoute
        @okHandler="onSubmit"
    >
        <loader :loader="isModalLoading" />
        <div v-show="!isModalLoading">
            <c-calculation
                :isCalculated.sync="$v.form.isCalculated.$model"
                :state="checkState('isCalculated')"
                :useProgressOnAutomaticCheckin.sync="$v.form.useProgressOnAutomaticCheckin.$model"
                :stateUseProgressOnAutomaticCheckin="checkState('useProgressOnAutomaticCheckin')"
                :formula.sync="$v.form.formula.$model"
                :stateFormula="checkState('formula')"
                :formulaHtml.sync="$v.form.formulaHtml.$model"
                :stateFormulaHtml="checkState('formulaHtml')"
                :is-kpi-available="currentUserHasKpiModule"
                :is-goal-available="currentUserHasGoalModule"
                :indicator-id="data.indicatorId"
                @ready="calculationReady = $event"
            >
                <span v-if="!$v.form.isCalculated.required">
                    {{ $t('form.validations.required') }}
                </span>
                <span v-else-if="!$v.form.isCalculated.manualOrAutomatic">
                    {{ $t('form.validations.invalidOption') }}
                </span>
            </c-calculation>
        </div>
        <template slot="okButton">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2"></i>
        </template>
    </Modal>
</template>

<script>
import api from '@/services/api'
import { getParam as getUrlParam } from '@/lib/url'
import notification, { success, danger } from '@/lib/notification'
import i18n from '@/i18n'
import Modal from '@/views/components/modal'
import { Calculation as CCalculation } from '@/views/components/fields'

import Loader from '@/views/components/loader'

// VALIDATIONS
import { validationMixin } from 'vuelidate'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { validFormula, manualOrAutomatic, checkinOrProgress } from '@/lib/validators'
import defaultIsCalculatedOptions from '@/data/calculation'
import defaultCheckinTypeOptions from '@/data/calculation-checkin-type-options'

notification.init()

export default {
    name: 'AutomaticCalculation',
    components: {
        Modal,
        Loader,
        CCalculation,
    },
    mixins: [validationMixin],
    props: ['data'],
    data() {
        return {
            loader: true,
            calculationReady: false,
            isSubmitting: false,
            form: {
                isCalculated: null,
                useProgressOnAutomaticCheckin: null,
                formula: null,
                formulaHtml: null,
            },
        }
    },
    methods: {
        checkState(val) {
            const field = this.$v.form[val] || val
            return !field.$dirty || !field.$invalid
        },
        onSubmit(event, closeModal) {
            event.preventDefault()
            if (this.validate()) {
                if (this.path) {
                    this.isSubmitting = true
                    api.put(this.path, this.mapperCalculationToAPI())
                        .then((resp) => {
                            const {
                                data: { data },
                            } = resp
                            this.updateParent(data)
                            this.notificationEditedSuccessfully()
                        })
                        .catch((error) => {
                            console.log(error)
                            this.notificationError()
                        })
                        .finally(() => {
                            this.isSubmitting = false
                            closeModal()
                        })
                } else {
                    this.notificationError()
                }
            }
        },
        validate() {
            this.$v.$touch()
            return !this.$v.$invalid
        },
        mapperCalculationToAPI() {
            const isCalculated = this.form.isCalculated.value == 1
            return {
                is_calculated: this.form.isCalculated.value,
                formula: isCalculated ? this.form.formula : '',
                formula_html: isCalculated ? this.form.formulaHtml : '',
                use_progress_on_automatic_checkin: isCalculated ? this.form.useProgressOnAutomaticCheckin.value : 0,
                profile_user_id: this.currentUser.id,
            }
        },
        getValue(object, property = 'value') {
            return object ? object[property] : null
        },
        notificationEditedSuccessfully() {
            let item = i18n.t('targets.target')
            if (this.data.module === 'kpi') {
                item = i18n.t('indicators.indicator')
            }
            success(
                i18n.t('notification.editedSuccessfully', {
                    item: item,
                })
            )
        },
        notificationError() {
            danger()
        },
        updateParent(data) {
            this.$emit('update-parent', 'updateCalculation', data)
        },
    },
    validations: {
        form: {
            formula: {
                validFormula,
                required: requiredIf((nestedModel) => {
                    const {
                        isCalculated: { value },
                    } = nestedModel
                    return value != 0
                }),
            },
            formulaHtml: {
                required: requiredIf((nestedModel) => {
                    const {
                        isCalculated: { value },
                    } = nestedModel
                    return value != 0
                }),
            },
            isCalculated: {
                required,
                manualOrAutomatic,
            },
            useProgressOnAutomaticCheckin: {
                checkinOrProgress,
            },
        },
    },
    computed: {
        isModalLoading() {
            return !this.calculationReady
        },
        disabledOkButton() {
            return this.isModalLoading || this.isSubmitting
        },
        disabledCancelButton() {
            return this.isSubmitting
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        currentUserHasKpiModule() {
            return this.currentUser.has_modules.includes('has_kpi')
        },
        currentUserHasGoalModule() {
            return this.currentUser.has_modules.includes('has_goal')
        },
        path() {
            let path = null
            switch (this.data.module) {
                case 'goal':
                    path = `/goals/${getUrlParam('targetId', this)}/update-formula`
                    break
                case 'kpi':
                    path = `/kpi/${getUrlParam('kpiId', this)}/update-formula`
                    break
            }
            return path
        },
    },
    watch: {
        calculationReady() {
            this.form.isCalculated = defaultIsCalculatedOptions.find((opt) => opt.value == this.data.isCalculated)
            this.form.useProgressOnAutomaticCheckin = defaultCheckinTypeOptions.find(
                (opt) => opt.value == this.data.useProgressOnAutomaticCheckin
            )
            this.form.formula = this.data.formula
            this.form.formulaHtml = this.data.formulaHtml
        },
    },
}
</script>
